import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import AuthLayout from './AuthLayout';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { useI18n } from '../context/useI18n';
import { getSanitizedPublicBrandName } from '../utils/branding';
import { clearAllModuleSessions } from '../utils/idleSession';
import { clearFitLiveIntent } from '../utils/fitLiveModule';
import { getAuthTenantBrand, pickLoginHeroFromBranding } from '../utils/documentBrand';

export default function ModernLogin({
  onSwitchToRegister,
  onSwitchToRegisterWithCode,
  onForgotPassword,
  orgSlug = '',
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showReactivateCta, setShowReactivateCta] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithEntrySso, logout } = useAuth();
  const { t } = useI18n();
  const { brandName } = useFrontendConfig();
  const trainerSlug = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const org = String(q.get('org') || orgSlug || '').trim();
      const svc = String(q.get('service') || '').toLowerCase();
      if (svc === 'training' && org) return org;
    } catch { /* noop */ }
    return '';
  })();
  const [tenantBrand, setTenantBrand] = useState(() => getAuthTenantBrand());
  const tenantHero = pickLoginHeroFromBranding(tenantBrand || {});
  const displayBrand = getSanitizedPublicBrandName(tenantHero.brand_name || brandName);

  useEffect(() => {
    const onBrand = (event) => {
      if (event?.detail && typeof event.detail === 'object') setTenantBrand(event.detail);
      else setTenantBrand(getAuthTenantBrand());
    };
    window.addEventListener('auth-tenant-brand', onBrand);
    return () => window.removeEventListener('auth-tenant-brand', onBrand);
  }, []);

  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const entrySso = qs.get('entry_sso');
      if (entrySso && typeof loginWithEntrySso === 'function') {
        setLoading(true);
        clearFitLiveIntent();
        loginWithEntrySso(entrySso)
          .then(() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('entry_sso');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          })
          .catch((err) => {
            setError(err?.response?.data?.error || 'No se pudo abrir la sesión del ecosistema.');
          })
          .finally(() => setLoading(false));
      }
      // Enlace de bienvenida / email: no reutilizar sesión admin u otra cuenta abierta.
      if (qs.get('force_logout') === '1' && !entrySso) {
        try { logout?.(); } catch { /* noop */ }
        clearAllModuleSessions();
      }
      const qsEmail = qs.get('email');
      const remembered = localStorage.getItem('rememberedEmail');
      const fromRegister = localStorage.getItem('prefillEmail');
      const prefill = qsEmail || remembered || fromRegister || '';
      if (prefill) {
        setEmail(prefill);
        if (fromRegister) localStorage.removeItem('prefillEmail');
      }
      const idleFlag = sessionStorage.getItem('shell_idle_logout') === '1';
      const idleReason = qs.get('reason') === 'idle';
      if (idleFlag || idleReason) {
        try { sessionStorage.removeItem('shell_idle_logout'); } catch { /* noop */ }
        setInfo(t(
          'auth.idle_logout',
          'Tu sesión se cerró por 1 hora de inactividad. Inicia sesión de nuevo.',
        ));
      }
      // Quitar ?reason=idle para que un refresh no deje el aviso anclado.
      if (idleReason || qs.has('reason')) {
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.get('reason') === 'idle') {
            url.searchParams.delete('reason');
            const next = `${url.pathname}${url.search}${url.hash}`;
            window.history.replaceState({}, '', next);
          }
        } catch { /* noop */ }
      }
    } catch (err) {
      console.warn('Failed to prefill modern login email', err);
    }
  }, [t, logout, loginWithEntrySso]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setShowReactivateCta(false);
    setLoading(true);
    try {
      await login(String(email).trim().toLowerCase(), password);
      try {
        localStorage.setItem('rememberedEmail', String(email).trim().toLowerCase());
      } catch (err) {
        console.warn('Failed to persist rememberedEmail', err);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(t('auth.rate_limit', 'Demasiados intentos. Espera unos minutos e intenta de nuevo.'));
      } else {
        const msg = err?.response?.data?.error || err?.message || 'Error en el login';
        const code = err?.response?.data?.code;
        const normalized = String(email).trim().toLowerCase();
        const deactivated = code === 'account_deactivated'
          || /desactivad/i.test(String(msg));
        if (deactivated) {
          try {
            localStorage.setItem('prefillEmail', normalized);
            localStorage.setItem('reactivateEmail', normalized);
          } catch { /* noop */ }
          setPassword('');
          setError(msg);
          setShowReactivateCta(true);
          setInfo(t(
            'auth.deactivated_hint',
            'Para reactivar tu cuenta debes registrarte de nuevo con el mismo correo (elige tu producto, p. ej. FOOD PLAN). No uses «Iniciar sesión» hasta completar ese registro.',
          ));
          // Evita el bucle login↔mensaje: lleva al registro con el email listo.
          if (typeof onSwitchToRegister === 'function') {
            window.setTimeout(() => {
              try { onSwitchToRegister(); } catch { /* noop */ }
            }, 400);
          }
        } else if (msg.toLowerCase().includes('incorrect') && /@gmial\.com$/i.test(normalized)) {
          setError(`${msg}. ¿Quisiste decir ${normalized.replace('@gmial.com', '@gmail.com')}?`);
        } else {
          setError(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      orgSlug={orgSlug}
      trainerSlug={trainerSlug}
      title={t('auth.login', 'Iniciar Sesión')}
      subtitle={`${t('auth.welcome', 'Bienvenido a')} ${displayBrand}`}
    >
      {info ? (
        <div className="rounded-lg border border-lime-500/40 bg-lime-950/30 text-lime-100 text-sm p-3 mb-2">{info}</div>
      ) : null}
      {error ? (
        <div className="error-message">{t('auth.login_error', 'Error en el login')}: {error}</div>
      ) : null}
      {showReactivateCta && typeof onSwitchToRegister === 'function' ? (
        <button
          type="button"
          className="btn btn-primary w-full mb-3"
          onClick={() => {
            try {
              const normalized = String(email).trim().toLowerCase();
              if (normalized) {
                localStorage.setItem('prefillEmail', normalized);
                localStorage.setItem('reactivateEmail', normalized);
              }
            } catch { /* noop */ }
            onSwitchToRegister();
          }}
        >
          {t('auth.reactivate_cta', 'Reactivar cuenta — ir a registro')}
        </button>
      ) : null}
      <form onSubmit={handleSubmit} className="auth-login-form space-y-5">
        <div className="form-group">
          <label htmlFor="email" className="label">{t('auth.email', 'Correo Electrónico')}</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => {
              setInfo('');
              setEmail(e.target.value);
            }}
            placeholder={t('auth.email_placeholder', 'tu@email.com')}
            className="input"
          />
        </div>
        <div className="form-group">
          <div className="flex items-center justify-between" style={{ marginBottom: '0.25rem' }}>
            <label htmlFor="password" className="label" style={{ marginBottom: 0 }}>{t('auth.password', 'Contraseña')}</label>
            <button type="button" onClick={onForgotPassword} className="link-button" style={{ fontSize: '0.75rem' }}>
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <div className="register-password-wrap">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setInfo('');
                setPassword(e.target.value);
              }}
              placeholder="••••••••"
              className="input"
            />
            <button
              type="button"
              className="register-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full"
          style={{ width: '100%', padding: '0.85rem' }}
        >
          {loading ? (
            <>
              <span
                aria-hidden="true"
                className="h-5 w-5 rounded-full border-2 border-black/25 border-t-black animate-spin"
              />
              <span>{t('auth.logging_in', 'Iniciando sesión...')}</span>
            </>
          ) : (
            <span>{t('auth.login', 'Iniciar Sesión')}</span>
          )}
        </button>
      </form>
      <div className="auth-login-footer mt-8 text-center text-sm" style={{ color: 'var(--d28d-muted)' }}>
        <p className="mb-3">
          {t('auth.no_account', '¿No tienes cuenta?')}{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="link-button"
          >
            {t('auth.register_free', 'Regístrate gratis')}
          </button>
        </p>
        {onSwitchToRegisterWithCode ? (
          <button
            type="button"
            onClick={onSwitchToRegisterWithCode}
            className="auth-invite-chip link-button font-medium"
          >
            {t('auth.register_with_code', 'Tengo código de invitación (gym, coach o programa)')}
          </button>
        ) : null}
      </div>
    </AuthLayout>
  );
}
